"use client";

import { useMounted } from "@/hooks/use-mounted";
import { cn } from "@/lib/utils";
import React, { useCallback, useEffect, useRef, useState } from "react";

export const FlipWords = ({
  words,
  duration = 3000,
  className,
}: {
  words: string[];
  duration?: number;
  className?: string;
}) => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const mounted = useMounted();
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // thanks for the fix Julian - https://github.com/Julian-AT
  const startAnimation = useCallback(() => {
    setIsExiting(true);
    
    // Wait for exit animation to complete
    setTimeout(() => {
      setCurrentWordIndex((prev) => (prev + 1) % words.length);
      setIsExiting(false);
    }, 300); // Exit animation duration
  }, [words.length]);

  useEffect(() => {
    if (mounted) {
      animationTimeoutRef.current = setTimeout(() => {
        startAnimation();
      }, duration);
    }

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [currentWordIndex, duration, startAnimation, mounted]);

  if (!mounted) return null;

  const currentWord = words[currentWordIndex];

  return (
    <div className="relative inline-block">
      <div
        className={cn(
          "z-10 inline-block relative text-left text-foreground px-2 transition-all duration-500",
          isExiting 
            ? "opacity-0 -translate-y-10 translate-x-10 scale-150 blur-sm absolute" 
            : "opacity-100 translate-y-0 translate-x-0 scale-100 blur-0 animate-in fade-in-0 slide-in-from-bottom-2",
          className,
        )}
        key={currentWord}
      >
        {/* edit suggested by Sajal: https://x.com/DewanganSajal */}
        {currentWord.split(" ").map((word, wordIndex) => (
          <span
            key={word + wordIndex}
            className="inline-block whitespace-nowrap"
          >
            {word.split("").map((letter, letterIndex) => (
              <span
                key={word + letterIndex}
                className="inline-block animate-in fade-in-0 slide-in-from-bottom-2 duration-200"
                style={{
                  animationDelay: `${wordIndex * 200 + letterIndex * 80}ms`,
                  animationFillMode: 'backwards',
                }}
              >
                {letter}
              </span>
            ))}
            <span className="inline-block">&nbsp;</span>
          </span>
        ))}
      </div>
    </div>
  );
};
